"use client";

import { m as motion } from "framer-motion";
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowUpRight,
  ArrowRight,
  ArrowDown,
  Crosshair,
  Wrench,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { InBreve } from "@/components/shared/InBreve";
import { ServiceMotif } from "@/components/services/ServiceMotif";
import {
  PortfolioBuildingVisual,
  CaseStudyTeaserVisual,
} from "./ProgettiVisuals";

interface ProgettiClientProps {
  isItalian: boolean;
  contactsHref: string;
  servicesHref: string;
}

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

/**
 * Link "Vedi le demo" ancora in lavorazione: non naviga da nessuna parte.
 * Al click mostra un piccolo avviso ("Sezione demo in lavorazione") invece di
 * portare a una pagina vuota. Sta SOPRA il link "stretched" della card (z-10)
 * e blocca la propagazione, cosi' non fa scattare la navigazione della card.
 */
function DemoLink({ isItalian }: { isItalian: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative z-10 mt-2">
      <button
        type="button"
        aria-disabled="true"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground/70 transition-colors hover:text-muted-foreground"
      >
        {isItalian ? "Vedi le demo" : "See the demos"}
        <span className="rounded-full border border-[hsl(var(--border))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground/80">
          In progress
        </span>
      </button>
      {open ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {isItalian
            ? "Sezione demo in lavorazione."
            : "Demo section in progress."}
        </p>
      ) : null}
    </div>
  );
}

export function ProgettiClient({
  isItalian,
  contactsHref,
  servicesHref,
}: ProgettiClientProps) {
  const buildTypes = [
    {
      slug: "siti-wordpress",
      motif: "gauge",
      title: isItalian ? "Siti WordPress" : "WordPress sites",
      desc: isItalian
        ? "Siti su misura, veloci, fatti per farti trovare su Google."
        : "Tailored, fast sites built to get you found on Google.",
    },
    {
      slug: "ecommerce-woocommerce",
      motif: "cart",
      title: isItalian ? "Ecommerce" : "Ecommerce",
      desc: isItalian
        ? "Negozi online che vendono davvero e restano tuoi."
        : "Online stores that really sell and stay yours.",
    },
    {
      slug: "sviluppo-custom-nextjs",
      motif: "code",
      title: isItalian ? "Web app e MVP" : "Web apps & MVP",
      desc: isItalian
        ? "La prima versione della tua idea, online e pronta a crescere."
        : "The first version of your idea, online and ready to grow.",
    },
    {
      slug: "app-mobile",
      motif: "phone",
      title: isItalian ? "App mobile" : "Mobile apps",
      desc: isItalian
        ? "Una sola app, su iPhone e Android. Codice tuo."
        : "One app, on iPhone and Android. Your code.",
    },
    {
      slug: "automazioni-n8n-ai",
      motif: "nodes",
      title: isItalian ? "Automazioni AI" : "AI automations",
      desc: isItalian
        ? "Flussi che tolgono il lavoro manuale che si ripete."
        : "Flows that remove the manual work that repeats.",
    },
    {
      slug: "siti-3d-creative",
      motif: "cube",
      title: isItalian ? "Esperienze 3D" : "3D experiences",
      desc: isItalian
        ? "Esperienze 3D web che le persone si ricordano."
        : "3D web experiences people actually remember.",
    },
    {
      slug: "bug-fixing-ai-nocode",
      motif: "rebuild",
      title: isItalian ? "Da no-code a prodotto" : "No-code to product",
      desc: isItalian
        ? "Prendo il tuo progetto no-code e lo rendo solido."
        : "I take your no-code project and make it solid.",
    },
  ];

  const storyFormat = [
    {
      icon: Crosshair,
      title: isItalian ? "Il problema" : "The problem",
      text: isItalian
        ? "Da dove parto: l'obiettivo del cliente, i vincoli reali e cosa non stava funzionando."
        : "Where I start: the client's goal, the real constraints and what wasn't working.",
    },
    {
      icon: Wrench,
      title: isItalian ? "La soluzione" : "The solution",
      text: isItalian
        ? "Cosa ho costruito e perché, con le scelte tecniche spiegate in parole semplici."
        : "What I built and why, with the technical choices explained in plain words.",
    },
    {
      icon: TrendingUp,
      title: isItalian ? "Il risultato" : "The result",
      text: isItalian
        ? "I numeri concreti: tempi, prestazioni, conversioni. Il prima e il dopo, senza giri di parole."
        : "The concrete numbers: time, performance, conversions. Before and after, no spin.",
    },
  ];

  const KICKER = isItalian
    ? "Prodotto interno Dimensione 4"
    : "Dimensione 4 internal product";
  const L = (it: string, en: string) => (isItalian ? it : en);

  const demos = [
    {
      cover: "/showcase/crm-shot.png",
      w: 1158,
      h: 645,
      title: L("CRM aziendale potenziato dall'AI", "AI-powered business CRM"),
      psr: [
        {
          label: L("Problema", "Problem"),
          text: L(
            "Follow-up dimenticati, preventivi in ritardo e ore perse a riscrivere le stesse email.",
            "Forgotten follow-ups, late quotes and hours lost rewriting the same emails.",
          ),
        },
        {
          label: L("Soluzione", "Solution"),
          text: L(
            "Un CRM su misura: l'AI segue le trattative, scrive le bozze nel tuo tono e ti manda tutto pronto su Telegram e WhatsApp. Tu approvi con un tap.",
            "A tailored CRM: AI tracks deals, drafts replies in your tone and sends everything ready on Telegram and WhatsApp. You approve with a tap.",
          ),
        },
        {
          label: L("Risultato", "Result"),
          text: L(
            "Zero follow-up persi e risposte in un secondo, mai più dal foglio bianco.",
            "Zero missed follow-ups and replies in a second, never from a blank page.",
          ),
        },
      ],
      stack: ["Next.js", "Postgres", "n8n", "AI", "Telegram", "WhatsApp"],
    },
    {
      cover: "/showcase/fiscale-shot.png",
      w: 1158,
      h: 769,
      title: L(
        "Fiscale D4 — simulatore forfettario",
        "Fiscale D4 — flat-tax simulator",
      ),
      psr: [
        {
          label: L("Problema", "Problem"),
          text: L(
            "Sapere quanto mettere da parte per imposta e contributi solo a fine anno, col rischio di brutte sorprese.",
            "Knowing how much to set aside for tax and contributions only at year end, risking nasty surprises.",
          ),
        },
        {
          label: L("Soluzione", "Solution"),
          text: L(
            "Calcola imposta sostitutiva, INPS e netto a ogni fattura, gestisce le scadenze F24 e avvisa su Telegram e WhatsApp.",
            "It computes flat tax, social contributions and net income on every invoice, tracks F24 deadlines and alerts on Telegram and WhatsApp.",
          ),
        },
        {
          label: L("Risultato", "Result"),
          text: L(
            "Sai sempre quant'è davvero tuo e arrivi alle scadenze già coperto.",
            "You always know what's really yours and reach deadlines already covered.",
          ),
        },
      ],
      stack: ["Next.js", "Postgres", "n8n", "Telegram", "WhatsApp"],
    },
    {
      cover: "/showcase/supporto-shot.png",
      w: 1158,
      h: 1277,
      title: L("Supporto D4 — ticketing con AI", "Supporto D4 — AI ticketing"),
      psr: [
        {
          label: L("Problema", "Problem"),
          text: L(
            "Richieste sparse tra email e chat, SLA che scadono e ticket che si perdono.",
            "Requests scattered across email and chat, SLAs expiring and tickets getting lost.",
          ),
        },
        {
          label: L("Soluzione", "Solution"),
          text: L(
            "Piattaforma di ticketing con triage AI, dashboard SLA, assistente vocale, pacchetti a pagamento e avvisi su Telegram e WhatsApp.",
            "A ticketing platform with AI triage, SLA dashboard, voice assistant, paid packages and Telegram/WhatsApp alerts.",
          ),
        },
        {
          label: L("Risultato", "Result"),
          text: L(
            "Ogni ticket al posto giusto prima che l'SLA scada, con la bozza di risposta già pronta.",
            "Every ticket in the right place before the SLA expires, with the reply draft already prepared.",
          ),
        },
      ],
      stack: ["Next.js", "Postgres", "AI", "Stripe", "Telegram", "WhatsApp"],
    },
    {
      cover: "/showcase/n8n-cover-2.png",
      w: 1128,
      h: 397,
      title: L(
        "Automazioni n8n — dalle call ai task",
        "n8n automations — from calls to tasks",
      ),
      psr: [
        {
          label: L("Problema", "Problem"),
          text: L(
            "Dopo ogni call, ore di data-entry per note e preventivi; task sparsi tra app diverse.",
            "After every call, hours of data entry for notes and quotes; tasks scattered across apps.",
          ),
        },
        {
          label: L("Soluzione", "Solution"),
          text: L(
            "Workflow n8n che trasformano le trascrizioni Meet in note e preventivi, e portano recap e comandi dei task su Telegram e WhatsApp.",
            "n8n workflows that turn Meet transcripts into notes and quotes, and bring task recaps and commands to Telegram and WhatsApp.",
          ),
        },
        {
          label: L("Risultato", "Result"),
          text: L(
            "Zero data-entry dopo le call, opportunità che non sfuggono, to-do dove già scrivi.",
            "Zero data entry after calls, opportunities that don't slip, to-dos where you already chat.",
          ),
        },
      ],
      stack: ["n8n", "Google Meet", "Company Brain", "CRM", "Telegram", "WhatsApp"],
    },
  ];

  return (
    <>
      {/* HERO */}
      <section className="section-padding pt-8 md:pt-24 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="container-wide relative">
          <div className="grid items-center gap-10 xl:grid-cols-[1.1fr_0.9fr] xl:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl text-left"
            data-snap-anchor
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-[hsl(var(--border))] text-label text-muted-foreground font-mono mb-4">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {isItalian ? "Portfolio in crescita" : "Portfolio in progress"}
            </span>
            <h1 className="page-hero-title mb-6">
              {isItalian ? (
                <>
                  Progetti che trasformano un&apos;idea in{" "}
                  <span className="text-shine">risultato</span>.
                </>
              ) : (
                <>
                  Projects that turn ideas into <span className="text-shine">outcomes</span>.
                </>
              )}
            </h1>
            <p className="text-body-lg text-muted-foreground max-w-2xl mb-8 mx-0">
              {isItalian
                ? "Sto raccogliendo qui i lavori veri, ognuno con problema, soluzione e risultati concreti. Intanto guarda cosa costruisco e come."
                : "I'm gathering the real work here, each with its problem, solution and concrete results. In the meantime, see what I build and how."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-start">
              <Button variant="hero" size="lg" asChild>
                <Link href={contactsHref}>
                  {isItalian ? "Avvia un progetto" : "Start a project"}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="rotating-border" asChild>
                <Link href={servicesHref}>
                  {isItalian ? "Vedi i servizi" : "View services"}
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Companion animato dell'hero: il portfolio che prende forma */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="w-full"
          >
            <PortfolioBuildingVisual />
          </motion.div>
          </div>
        </div>
      </section>

      <InBreve
        text={
          isItalian
            ? "Qui trovi i progetti che ho costruito davvero: siti, negozi online, app web, primi prototipi funzionanti ed esperienze in 3D. Per ognuno vedi cos'era il problema, cosa ho realizzato e che risultati ha portato, non solo qualche immagine. Lavori seguiti da una sola persona, dall'idea al sito online, con codice e proprietà che restano tuoi. Guarda i casi che assomigliano al tuo e, se ti convincono, prenota una chiamata gratuita: ti dico subito come affronterei il tuo progetto."
            : "Here you'll find the projects I've actually built: websites, online shops, web apps, first working prototypes and 3D experiences. For each one you can see what the problem was, what I made and the results it brought, not just a few pictures. Work handled by one person, from the idea to the live site, with the code and ownership staying yours. Look at the cases that resemble yours and, if they win you over, book a free call: I'll tell you right away how I'd tackle your project."
        }
      />

      {/* COSA COSTRUISCO */}
      <section className="section-padding pt-0">
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-3xl text-left">
            <span className="font-mono text-label text-primary mb-3 block">
              {isItalian ? "Cosa costruisco" : "What I build"}
            </span>
            <h2 className="font-display font-bold whitespace-nowrap text-[clamp(1.5rem,6vw,2.75rem)] leading-[1.15] tracking-[-0.01em]">
              {isItalian ? "Cosa costruisco, per tipo" : "What I build, by type"}
            </h2>
          </motion.div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {buildTypes.map((t, i) => (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
              >
                <div className="group relative flex h-full flex-col rounded-xl border border-[hsl(var(--border))] bg-surface/40 p-6 transition-colors hover:border-primary/40 hover:bg-surface">
                  <div className="relative mb-4 h-12 w-12">
                    <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-xl" />
                    <div className="relative h-full w-full">
                      <ServiceMotif variant={t.motif} />
                    </div>
                  </div>
                  <h3 className="font-display text-base font-semibold mb-1.5 transition-colors group-hover:text-primary">
                    {t.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{t.desc}</p>

                  {/*
                    Slot demo per-card: spazio riservato per una futura demo
                    (mini-preview / video) del progetto. Reso solo se la card
                    avra' una demo (t.demo), per ora assente -> niente contenuto
                    finto. La struttura e' pronta, niente layout shift.
                  */}
                  {"demo" in t && (t as { demo?: unknown }).demo ? (
                    <div className="mt-4 overflow-hidden rounded-lg border border-[hsl(var(--border))] bg-background/30">
                      {/* slot demo (placeholder strutturale) */}
                    </div>
                  ) : null}

                  {/* CTA principale: link "stretched" (after:inset-0) -> tutta
                      la card resta cliccabile verso il servizio. */}
                  <Link
                    href={`${servicesHref}/${t.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary after:absolute after:inset-0 after:content-['']"
                  >
                    {isItalian ? "Scopri il servizio" : "Explore the service"}
                    <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
                  </Link>

                  {/* Link demo: per ora in lavorazione, non naviga */}
                  <DemoLink isItalian={isItalian} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO PRODOTTO */}
      <section className="section-padding pt-16 md:pt-24">
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-3xl text-left">
            <span className="font-mono text-label text-primary mb-3 block">
              {isItalian ? "Prodotti interni" : "Internal products"}
            </span>
            <h2 className="font-display font-bold text-[clamp(1.5rem,6vw,2.75rem)] leading-[1.15] tracking-[-0.01em]">
              {isItalian
                ? "Prodotti veri che ho costruito"
                : "Real products I've built"}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {isItalian
                ? "Schermate rappresentative con dati d'esempio. Per ognuno: problema, soluzione e stack."
                : "Representative screens with sample data. For each: problem, solution and stack."}
            </p>
          </motion.div>

          <div className="space-y-6">
            {demos.map((d, i) => (
              <motion.div key={d.title} {...fadeUp}>
                <div className="grid overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface/40 xl:grid-cols-2">
                  <div
                    className={`flex items-center justify-center border-b border-[hsl(var(--border))] bg-[#0a0e12] p-5 md:p-7 xl:border-b-0 xl:border-r ${
                      i % 2 === 1
                        ? "xl:order-2 xl:border-l xl:border-r-0"
                        : ""
                    }`}
                  >
                    <Image
                      src={d.cover}
                      alt={d.title}
                      width={d.w}
                      height={d.h}
                      className="h-auto w-full max-w-full rounded-lg border border-[hsl(var(--border))]"
                    />
                  </div>

                  <div className="flex flex-col p-6 md:p-8">
                    <span className="mb-2 font-mono text-label text-muted-foreground">
                      {KICKER}
                    </span>
                    <h3 className="mb-5 font-display text-xl font-bold md:text-2xl">
                      {d.title}
                    </h3>
                    <dl className="space-y-3">
                      {d.psr.map((row) => (
                        <div key={row.label}>
                          <dt className="font-mono text-[11px] uppercase tracking-wide text-primary/80">
                            {row.label}
                          </dt>
                          <dd className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                            {row.text}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {d.stack.map((s) => (
                        <span
                          key={s}
                          className="rounded-md border border-[hsl(var(--border))] bg-surface/70 px-2 py-1 font-mono text-[11px] text-muted-foreground"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COME RACCONTO UN PROGETTO */}
      <section className="section-padding pt-16 md:pt-24">
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-12 max-w-3xl text-left">
            <span className="font-mono text-label text-primary mb-3 block">
              {isItalian ? "Come li racconto" : "How I tell them"}
            </span>
            <h2 className="font-display font-bold whitespace-nowrap text-[clamp(1.5rem,6vw,2.75rem)] leading-[1.15] tracking-[-0.01em]">
              {isItalian ? "Ogni progetto, in tre passi" : "Each project, in three steps"}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {isItalian
                ? "Niente gallerie di immagini fini a sé stesse. Per ogni lavoro trovi cosa c'era da risolvere, cosa ho fatto e i numeri che ha portato."
                : "No image galleries for their own sake. For each project you'll find what needed solving, what I did and the numbers it brought."}
            </p>
          </motion.div>

          {/* Tre passi su una riga: Problema -> Soluzione -> Risultato */}
          <div className="grid items-stretch gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
            {storyFormat.map((s, i) => (
              <div key={s.title} className="contents">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className="surface-card p-6"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-glow text-primary-foreground">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </motion.div>

                {/* Connettore tra i passi (freccia ciano), nascosto dopo l'ultimo */}
                {i < storyFormat.length - 1 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: i * 0.1 + 0.2 }}
                    className="flex items-center justify-center text-primary"
                    aria-hidden
                  >
                    <ArrowRight className="hidden h-5 w-5 md:block" />
                    <ArrowDown className="h-5 w-5 md:hidden" />
                  </motion.div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEASER: CASE STUDY IN ARRIVO */}
      <section className="section-padding pt-16 md:pt-24">
        <div className="container-wide">
          <motion.div
            {...fadeUp}
            className="surface-card relative overflow-hidden p-6 md:p-8"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative grid items-center gap-8 text-left xl:grid-cols-[1.4fr_1fr]">
              <div>
                <span className="mb-3 inline-flex items-center gap-2 font-mono text-label text-primary">
                  <Sparkles className="h-4 w-4" />
                  {isItalian ? "Primi case study in arrivo" : "First case studies coming"}
                </span>
                <h2 className="font-display text-2xl md:text-3xl font-bold">
                  {isItalian
                    ? "Vuoi essere tra i primi lavori raccontati qui?"
                    : "Want to be among the first projects featured here?"}
                </h2>
                <p className="mt-3 max-w-xl text-muted-foreground">
                  {isItalian
                    ? "Sto chiudendo i primi progetti da pubblicare. Se partiamo adesso, il tuo potrebbe essere uno di questi: codice tuo, risultati misurabili, raccontati in chiaro."
                    : "I'm wrapping up the first projects to publish. If we start now, yours could be one of them: your code, measurable results, told clearly."}
                </p>
                <div className="mt-6">
                  <Button variant="hero" size="lg" asChild>
                    <Link href={contactsHref}>
                      {isItalian ? "Parliamone" : "Let's talk"}
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <CaseStudyTeaserVisual />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </>
  );
}
